import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  DollarSign, TrendingUp, Building2, Loader2, Users, ArrowLeft,
  Receipt, CheckCircle2, Clock, AlertTriangle, Calendar,
  Shield, CreditCard, LogIn, UserCog, Search, Eye,
} from "lucide-react";
import {
  BarChart, Bar, PieChart as RPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  ComposedChart, Line,
} from "recharts";
import { AdminLayout } from "../../components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { cn } from "../../lib/utils";

const COLORS = ["#22c55e", "#3b82f6", "#a855f7", "#f59e0b"];
const fmt = (n) => {
  if (n == null) return "$0";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
  return `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const TABS = [
  { id: "overview", label: "Overview" },
  { id: "users", label: "Users" },
  { id: "invoices", label: "Invoices" },
];

const PERIOD_TABS = [
  { id: "overall", label: "Overall" },
  { id: "this_year", label: "This Year" },
  { id: "this_quarter", label: "This Quarter" },
  { id: "this_month", label: "This Month" },
];

const TenantRevenuePage = () => {
  const { tenantId } = useParams();
  const nav = useNavigate();
  const { user: currentUser } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [periodTab, setPeriodTab] = useState("overall");
  const [trialDialog, setTrialDialog] = useState(false);
  const [trialDialogAction, setTrialDialogAction] = useState("start");
  const [trialDays, setTrialDays] = useState(14);
  const [trialLoading, setTrialLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [staff, setStaff] = useState([]);
  const [am, setAm] = useState(null);
  const [pm, setPm] = useState(null);
  const [userSearch, setUserSearch] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/financial/tenant/${tenantId}/revenue?period=${periodTab}`);
      setData(r.data);
      setAm(r.data.tenant?.account_manager || null);
      setPm(r.data.tenant?.project_manager || null);
    } catch { toast.error("Failed to load tenant revenue"); }
    setLoading(false);
  }, [tenantId, periodTab]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    api.get("/admin/financial/staff").then(r => setStaff(r.data || [])).catch(() => {});
  }, []);

  const handleTrial = async (action) => {
    setTrialLoading(true);
    try {
      const r = await api.post(`/admin/financial/trial/${tenantId}`, { action, days: trialDays });
      toast.success(r.data.message);
      setTrialDialog(false);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setTrialLoading(false);
  };

  const handleImpersonate = async (targetUser) => {
    if (!window.confirm(`Impersonate ${targetUser.full_name || targetUser.email}?\nYou will be logged in as this user.`)) return;
    setImpersonating(true);
    try {
      const r = await api.post(`/admin/impersonate/${targetUser.id}`);
      localStorage.setItem("odesie_impersonation", JSON.stringify({
        original_admin_id: currentUser?.id,
        original_token: localStorage.getItem("odesie_token") || localStorage.getItem("token"),
        target_name: targetUser.full_name,
      }));
      localStorage.setItem("odesie_token", r.data.access_token);
      localStorage.setItem("token", r.data.access_token);
      toast.success(`Now impersonating ${targetUser.full_name || targetUser.email}`);
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Impersonation failed"); }
    setImpersonating(false);
  };

  const handleSaveManagers = async () => {
    try {
      await api.put(`/admin/financial/tenant/${tenantId}/managers`, {
        account_manager: am, project_manager: pm,
      });
      toast.success("Managers updated");
    } catch { toast.error("Failed to update managers"); }
  };

  if (loading) return (
    <AdminLayout title="Tenant Revenue">
      <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
    </AdminLayout>
  );

  if (!data) return null;
  const { tenant, metrics, billing, revenue_sources, monthly_history, users } = data;
  const billingStatus = tenant.billing_status || "active";
  const isFree = billingStatus === "free";
  const isTrial = tenant.trial_active || billingStatus === "trial";
  const isPaid = !isFree && !isTrial;
  const SC = { paid: "text-green-700 bg-green-50", pending: "text-amber-700 bg-amber-50", overdue: "text-red-700 bg-red-50" };
  const filteredUsers = (users || []).filter(u => {
    if (!userSearch) return true;
    const q = userSearch.toLowerCase();
    return (u.full_name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
  });

  return (
    <AdminLayout title={`${tenant.name} — Revenue`}>
      <div className="space-y-5 max-w-[1400px]" data-testid="tenant-revenue-page">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button size="sm" variant="ghost" className="h-8" onClick={() => nav("/admin/revenue")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold">{tenant.name}</h1>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge variant="outline" className="text-[9px] capitalize">{tenant.status}</Badge>
                <Badge variant="outline" className="text-[9px] capitalize">{tenant.plan_id}</Badge>
                {isFree && <Badge className="text-[9px] bg-green-50 text-green-700 border-green-200" data-testid="badge-free">Free</Badge>}
                {isTrial && <Badge className="text-[9px] bg-amber-50 text-amber-700 border-amber-200" data-testid="badge-trial">Trial{tenant.trial_end ? ` (ends ${tenant.trial_end.slice(0, 10)})` : ""}</Badge>}
                {isPaid && <Badge className="text-[9px] bg-blue-50 text-blue-700 border-blue-200" data-testid="badge-paid">Paid</Badge>}
              </div>
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {isFree && (
              <>
                <Button size="sm" className="h-7 text-[11px] gap-1" onClick={() => handleTrial("convert_to_paid")} disabled={trialLoading} data-testid="convert-to-paid-btn"><DollarSign className="w-3 h-3" /> Convert to Paid</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => { setTrialDialogAction("start"); setTrialDialog(true); }} data-testid="start-trial-btn"><Clock className="w-3 h-3" /> Start Trial</Button>
              </>
            )}
            {isTrial && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => { setTrialDialogAction("extend"); setTrialDialog(true); }} data-testid="extend-trial-btn"><Clock className="w-3 h-3" /> Extend Trial</Button>
                <Button size="sm" className="h-7 text-[11px] gap-1" onClick={() => handleTrial("convert_to_paid")} disabled={trialLoading} data-testid="convert-to-paid-btn"><DollarSign className="w-3 h-3" /> Convert to Paid</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1 text-red-600" onClick={() => handleTrial("end")} disabled={trialLoading} data-testid="end-trial-btn">End Trial</Button>
              </>
            )}
            {isPaid && (
              <>
                <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => handleTrial("mark_free")} disabled={trialLoading} data-testid="mark-free-btn"><Shield className="w-3 h-3" /> Mark as Free</Button>
                <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={() => { setTrialDialogAction("start"); setTrialDialog(true); }} data-testid="start-trial-btn"><Clock className="w-3 h-3" /> Start Trial</Button>
              </>
            )}
          </div>
        </div>

        {/* Account Manager Assignment */}
        <Card className="border shadow-none" data-testid="manager-assignment">
          <CardContent className="p-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 min-w-[240px]">
                <UserCog className="w-4 h-4 text-muted-foreground shrink-0" />
                <Label className="text-xs shrink-0">Account Manager</Label>
                <Select value={am || "unassigned"} onValueChange={v => setAm(v === "unassigned" ? null : v)}>
                  <SelectTrigger className="h-7 text-xs w-44" data-testid="am-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name || s.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 min-w-[240px]">
                <UserCog className="w-4 h-4 text-muted-foreground shrink-0" />
                <Label className="text-xs shrink-0">Project Manager</Label>
                <Select value={pm || "unassigned"} onValueChange={v => setPm(v === "unassigned" ? null : v)}>
                  <SelectTrigger className="h-7 text-xs w-44" data-testid="pm-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {staff.map(s => <SelectItem key={s.id} value={s.id}>{s.full_name || s.email}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <Button size="sm" className="h-7 text-[11px]" onClick={handleSaveManagers} data-testid="save-managers-btn">
                <CheckCircle2 className="w-3 h-3 mr-1" /> Save
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Page Tabs */}
        <div className="flex items-center gap-1 border-b">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={cn("px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors",
                tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground")}
              data-testid={`page-tab-${t.id}`}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ═══ OVERVIEW TAB ═══ */}
        {tab === "overview" && (
          <div className="space-y-5">
            {/* Period Tabs */}
            <div className="flex items-center gap-1">
              {PERIOD_TABS.map(t => (
                <Button key={t.id} size="sm" variant={periodTab === t.id ? "default" : "outline"}
                  className="h-7 text-[11px]" onClick={() => setPeriodTab(t.id)} data-testid={`tenant-tab-${t.id}`}>
                  {t.label}
                </Button>
              ))}
            </div>

            {/* KPI Row */}
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {[
                { label: "MRR", value: fmt(metrics.mrr), icon: DollarSign, color: "text-green-600 bg-green-50" },
                { label: "ARR", value: fmt(metrics.arr), icon: TrendingUp, color: "text-blue-600 bg-blue-50" },
                { label: "Active Users", value: metrics.active_users, icon: Users, color: "text-violet-600 bg-violet-50", sub: `${metrics.billable_users} billable` },
                { label: "Courses", value: metrics.courses, icon: Building2, color: "text-amber-600 bg-amber-50" },
                { label: "ILT Sessions", value: metrics.ilt_sessions, icon: Calendar, color: "text-teal-600 bg-teal-50" },
                { label: "Certifications", value: metrics.certifications, icon: CreditCard, color: "text-rose-600 bg-rose-50" },
              ].map(k => (
                <Card key={k.label} className="border shadow-none"><CardContent className="p-4">
                  <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center mb-2", k.color)}><k.icon className="w-4 h-4" /></div>
                  <p className="text-xl font-bold">{k.value}</p><p className="text-[10px] text-muted-foreground">{k.label}</p>
                  {k.sub && <p className="text-[9px] text-muted-foreground">{k.sub}</p>}
                </CardContent></Card>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <Card className="border shadow-none lg:col-span-2" data-testid="tenant-monthly-chart">
                <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Monthly Revenue Breakdown</CardTitle></CardHeader>
                <CardContent className="px-2 pb-3">
                  <ResponsiveContainer width="100%" height={240}>
                    <ComposedChart data={monthly_history}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 9 }} tickFormatter={v => v?.split(" ")[0]} />
                      <YAxis tick={{ fontSize: 10 }} tickFormatter={v => fmt(v)} width={55} />
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                      <Bar dataKey="subscription" fill="#22c55e" stackId="a" name="Subscription" barSize={24} />
                      <Bar dataKey="ilt" fill="#3b82f6" stackId="a" name="ILT" />
                      <Bar dataKey="certifications" fill="#a855f7" stackId="a" name="Certifications" />
                      <Line type="monotone" dataKey="total" stroke="#111" strokeWidth={2} dot={{ r: 2 }} name="Total" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
              <Card className="border shadow-none" data-testid="tenant-source-chart">
                <CardHeader className="pt-4 pb-1 px-5"><CardTitle className="text-sm">Revenue Sources</CardTitle></CardHeader>
                <CardContent className="px-2 pb-3">
                  <ResponsiveContainer width="100%" height={240}>
                    <RPieChart>
                      <Pie data={revenue_sources} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={75} paddingAngle={3}>
                        {revenue_sources?.map((s, i) => <Cell key={i} fill={s.color || COLORS[i]} />)}
                      </Pie>
                      <Tooltip formatter={v => fmt(v)} />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 10 }} />
                    </RPieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </div>

            {/* Billing Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
              <Card className="border shadow-none"><CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-50 text-green-600 flex items-center justify-center"><CheckCircle2 className="w-5 h-5" /></div>
                <div><p className="text-lg font-bold">{fmt(billing.total_paid)}</p><p className="text-[10px] text-muted-foreground">Total Paid</p></div>
              </CardContent></Card>
              <Card className="border shadow-none"><CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center"><Clock className="w-5 h-5" /></div>
                <div><p className="text-lg font-bold">{fmt(billing.total_outstanding)}</p><p className="text-[10px] text-muted-foreground">Outstanding</p></div>
              </CardContent></Card>
              <Card className="border shadow-none"><CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center"><Receipt className="w-5 h-5" /></div>
                <div><p className="text-lg font-bold">{fmt(billing.total_invoiced)}</p><p className="text-[10px] text-muted-foreground">Total Invoiced</p></div>
              </CardContent></Card>
            </div>
          </div>
        )}

        {/* ═══ USERS TAB ═══ */}
        {tab === "users" && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">{(users || []).length} users in this tenant</p>
              <div className="relative w-64">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-8 h-8 text-sm" data-testid="user-search" />
              </div>
            </div>
            <Card className="border shadow-none" data-testid="tenant-users-table">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {["Name", "Email", "Role", "Department", "Status", "Last Login", "Actions"].map(h => (
                      <th key={h} className="p-2.5 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="border-b hover:bg-muted/20 cursor-pointer" onClick={() => navigate(`/admin/team/${u.id}`)}>
                        <td className="p-2.5 text-sm font-medium">{u.full_name || "—"}</td>
                        <td className="p-2.5 text-xs text-muted-foreground">{u.email}</td>
                        <td className="p-2.5"><Badge variant="outline" className="text-[9px] capitalize">{u.role}</Badge></td>
                        <td className="p-2.5 text-xs">{u.department || "—"}</td>
                        <td className="p-2.5"><Badge className={cn("text-[9px]", u.is_active ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700")}>{u.is_active ? "Active" : "Inactive"}</Badge></td>
                        <td className="p-2.5 text-xs text-muted-foreground">{u.last_login ? u.last_login.slice(0, 10) : "Never"}</td>
                        <td className="p-2.5" onClick={e => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" onClick={() => navigate(`/admin/team/${u.id}`)} data-testid={`view-profile-${u.id}`}>
                              <Eye className="w-3 h-3" /> Profile
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 text-[10px] gap-1" onClick={() => handleImpersonate(u)} disabled={impersonating} data-testid={`impersonate-${u.id}`}>
                              <LogIn className="w-3 h-3" /> Impersonate
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-sm text-muted-foreground">No users found</td></tr>}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        )}

        {/* ═══ INVOICES TAB ═══ */}
        {tab === "invoices" && (
          <Card className="border shadow-none" data-testid="tenant-invoices">
            <CardHeader className="pt-4 pb-2 px-5"><CardTitle className="text-sm">Invoice History</CardTitle></CardHeader>
            <CardContent className="px-5 pb-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead><tr className="border-b bg-muted/30">
                    {["Invoice", "Period", "Due Date", "Amount", "Users", "Status"].map(h => (
                      <th key={h} className="p-2 text-[10px] font-semibold uppercase text-muted-foreground text-left">{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {billing.invoices?.map(inv => (
                      <tr key={inv.id} className="border-b hover:bg-muted/20">
                        <td className="p-2 text-xs font-mono">{inv.id}</td>
                        <td className="p-2 text-xs">{inv.period_label}</td>
                        <td className="p-2 text-xs">{inv.due_date?.slice(0, 10)}</td>
                        <td className="p-2 text-sm font-bold">${inv.amount?.toLocaleString()}</td>
                        <td className="p-2 text-xs">{inv.billable_users}</td>
                        <td className="p-2"><Badge className={cn("text-[9px] capitalize border", SC[inv.status] || "bg-gray-50")}>{inv.status}</Badge></td>
                      </tr>
                    ))}
                    {(!billing.invoices || billing.invoices.length === 0) && <tr><td colSpan={6} className="p-6 text-center text-sm text-muted-foreground">No invoices</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Trial Dialog */}
        {trialDialog && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-sm shadow-xl">
              <h3 className="text-lg font-bold mb-3">{trialDialogAction === "extend" ? "Extend Trial" : "Start Free Trial"}</h3>
              <label className="text-xs text-muted-foreground">Duration (days)</label>
              <input type="number" value={trialDays} onChange={e => setTrialDays(parseInt(e.target.value) || 0)} className="w-full border rounded px-3 py-2 text-sm mt-1 mb-4" data-testid="trial-days-input" />
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => setTrialDialog(false)}>Cancel</Button>
                <Button size="sm" onClick={() => handleTrial(trialDialogAction)} disabled={trialLoading} data-testid="confirm-trial-btn">
                  {trialLoading ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                  {trialDialogAction === "extend" ? "Extend" : "Start Trial"}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default TenantRevenuePage;
